/**
 * Peke Level - Activity: Peke's Adventures
 * Interactive storytelling with Peke the Boston Terrier
 * Ages 3-5 - Voice-based story creation
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
import { VoiceEnableButton } from '../../ui/VoiceEnableButton';
import { soundManager } from '../../../lib/soundManager';
import { Mic, Play, Pause } from 'lucide-react';
import PekeSurvey from './PekeSurvey';
import { track } from '@vercel/analytics';

// Peke watercolour illustrations.
// Each story slide ships a 1600w master + an 800w mobile variant. The
// `<img srcSet>` calls below let the browser pick the right one per
// device-pixel-ratio so phones don't pull down the 1.5MB desktop hero.
import pekeIntro from '../../../assets/animations/peke/optimized/Intro-Peke.jpg';
import pekeDream from '../../../assets/animations/peke/optimized/Peke-1s.jpg';
import pekeDreamSm from '../../../assets/animations/peke/optimized/Peke-1s-sm.jpg';
import pekeThinking from '../../../assets/animations/peke/optimized/Peke-2s.jpg';
import pekeThinkingSm from '../../../assets/animations/peke/optimized/Peke-2s-sm.jpg';
import pekeCelebrating from '../../../assets/animations/peke/optimized/Peke-3s.jpg';
import pekeCelebratingSm from '../../../assets/animations/peke/optimized/Peke-3s-sm.jpg';
import pekeCelebration from '../../../assets/animations/peke/optimized/celebration-Peke.jpg';

type GameStage = 'intro' | 'story' | 'final-story' | 'complete' | 'survey';

interface StorySlide {
  id: number;
  /** 1600w master — used by `<img src>` and the `1600w` srcSet entry. */
  image: string;
  /** 800w mobile variant — used by the `800w` srcSet entry. */
  imageSm: string;
  question: {
    en: string;
    es: string;
  };
  prompt: {
    en: string;
    es: string;
  };
}

interface PekeLevelProps {
  onBackToHome?: () => void;
}

export function PekeLevel({ onBackToHome }: PekeLevelProps) {
  const { language, setLanguage } = useLanguage();

  // Story slides configuration
  const storySlides: StorySlide[] = [
    {
      id: 1,
      image: pekeDream,
      imageSm: pekeDreamSm,
      question: {
        en: "What is Peke dreaming about?",
        es: "¿Con qué está soñando Peke?"
      },
      prompt: {
        en: "Tell me about Peke's dream...",
        es: "Cuéntame sobre el sueño de Peke..."
      }
    },
    {
      id: 2,
      image: pekeThinking,
      imageSm: pekeThinkingSm,
      question: {
        en: "What thoughts and emotions might be flowing through Peke's body right now?",
        es: "¿Qué pensamientos y emociones podrían estar fluyendo por el cuerpo de Peke ahora mismo?"
      },
      prompt: {
        en: "Share what's in Peke's heart...",
        es: "Comparte lo que hay en el corazón de Peke..."
      }
    },
    {
      id: 3,
      image: pekeCelebrating,
      imageSm: pekeCelebratingSm,
      question: {
        en: "What might have brought this big, glowing smile and giggles to Peke's face?",
        es: "¿Qué podría haber traído esta gran sonrisa radiante y risitas a la cara de Peke?"
      },
      prompt: {
        en: "Tell me the joyful ending...",
        es: "Cuéntame el final alegre..."
      }
    }
  ];

  // Translations for all UI text
  const translations = {
    en: {
      home: 'Home',
      hello: "Hello! I'm Peke",
      intro: "I'm a Boston Terrier and I have a story to share...",
      question: 'But I need YOUR help to tell my story!',
      yes: "Yes! Let's create the story!",
      recording: 'Recording...',
      tapToRecord: 'Tap to Record Your Answer',
      tapToStop: 'Tap to Stop Recording',
      listening: 'Listening to your story...',
      awesome: 'Awesome story!',
      nextSlide: 'Next Part',
      createStory: 'See My Story',
      yourStoryTitle: "Your Amazing Story with Peke!",
      storyIntro: "Here's the wonderful story you created:",
      part: 'Part',
      replay: 'Tell Story Again',
      newStory: 'Create New Story',
      wonderful: 'You\'re a wonderful storyteller!',
      thankYou: 'Thank you for sharing this adventure with Peke!',
      comeBack: 'Come back to create more stories!',
      again: 'Create Another Story',
      goHome: 'Home',
      recordingInstructions: 'Tap or press the microphone to record and tell your answer for the story!',
      playback: 'Listen to Your Answer',
      previous: 'Previous',
      next: 'Next',
      continue: 'Continue',
    },
    es: {
      home: 'Inicio',
      hello: '¡Hola! Soy Peke',
      intro: 'Soy un Boston Terrier y tengo una historia que compartir...',
      question: '¡Pero necesito TU ayuda para contar mi historia!',
      yes: '¡Sí! ¡Vamos a crear la historia!',
      recording: 'Grabando...',
      tapToRecord: 'Toca para Grabar tu Respuesta',
      tapToStop: 'Toca para Detener Grabación',
      listening: 'Escuchando tu historia...',
      awesome: '¡Historia increíble!',
      nextSlide: 'Siguiente Parte',
      createStory: 'Ver Mi Historia',
      yourStoryTitle: '¡Tu Historia Increíble con Peke!',
      storyIntro: 'Aquí está la maravillosa historia que creaste:',
      part: 'Parte',
      replay: 'Contar Historia Otra Vez',
      newStory: 'Crear Nueva Historia',
      wonderful: '¡Eres un narrador maravilloso!',
      thankYou: '¡Gracias por compartir esta aventura con Peke!',
      comeBack: '¡Vuelve para crear más historias!',
      again: 'Crear Otra Historia',
      goHome: 'Inicio',
      recordingInstructions: '¡Toca o presiona el micrófono para grabar y contar tu respuesta para la historia!',
      playback: 'Escucha Tu Respuesta',
      previous: 'Anterior',
      next: 'Siguiente',
      continue: 'Continuar',
    },
  };

  const t = translations[language];

  const [stage, setStage] = useState<GameStage>('intro');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [storyReviewSlide, setStoryReviewSlide] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState<boolean[]>([false, false, false]);
  const [recordings, setRecordings] = useState<(Blob | null)[]>([null, null, null]);
  const [audioURLs, setAudioURLs] = useState<(string | null)[]>([null, null, null]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameStartTime] = useState<number>(Date.now());

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Track game start
  useEffect(() => {
    if (stage === 'story') {
      track('Game Started', {
        game: 'peke-level',
        gameName: "Peke's Adventures",
        language: language
      });
    }
  }, [stage, language]);

  // Track game completion
  useEffect(() => {
    if (stage === 'complete') {
      track('Game Completed', {
        game: 'peke-level',
        gameName: "Peke's Adventures",
        slidesCompleted: storySlides.length,
        language: language
      });
    }
  }, [stage, language, storySlides.length]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Update recordings and URLs
        const newRecordings = [...recordings];
        newRecordings[currentSlide] = audioBlob;
        setRecordings(newRecordings);

        const newAudioURLs = [...audioURLs];
        newAudioURLs[currentSlide] = audioUrl;
        setAudioURLs(newAudioURLs);

        const newHasRecorded = [...hasRecorded];
        newHasRecorded[currentSlide] = true;
        setHasRecorded(newHasRecorded);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        soundManager.playCelebration();
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please allow microphone permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Play recorded audio
  const playRecording = (index: number) => {
    if (audioURLs[index]) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      const audio = new Audio(audioURLs[index]!);
      audioRef.current = audio;

      audio.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
      };

      audio.play();
      setIsPlaying(true);
    }
  };

  // Stop playback
  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioURLs.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const handleNextStage = () => {
    if (stage === 'intro') {
      setStage('story');
    }
  };

  const handleNextSlide = () => {
    if (currentSlide < storySlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
      stopPlayback();
      soundManager.playSparkleSequence(3, 150);
    } else {
      // All slides complete, show final story
      setStage('final-story');
      setStoryReviewSlide(0);
      soundManager.playCelebration();
      soundManager.playSparkleSequence(15, 100);
    }
  };

  const handleComplete = () => {
    setStage('survey');
    soundManager.playCelebration();
  };

  const handleSurveyComplete = () => {
    // After survey is complete, show celebration with options
    setStage('complete');
    soundManager.playCelebration();
    soundManager.playSparkleSequence(20, 100);
  };

  const getGameDurationSeconds = (): number => {
    return Math.floor((Date.now() - gameStartTime) / 1000);
  };

  const handleReplay = () => {
    setStage('intro');
    setCurrentSlide(0);
    setStoryReviewSlide(0);
    setHasRecorded([false, false, false]);
    setRecordings([null, null, null]);
    audioURLs.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    setAudioURLs([null, null, null]);
    stopPlayback();
  };

  const handlePreviousStorySlide = () => {
    if (storyReviewSlide > 0) {
      setStoryReviewSlide(storyReviewSlide - 1);
      stopPlayback();
    }
  };

  const handleNextStorySlide = () => {
    if (storyReviewSlide < storySlides.length - 1) {
      setStoryReviewSlide(storyReviewSlide + 1);
      stopPlayback();
    }
  };

  // ── Design tokens — shared with the rest of the games ───────────────────
  const INK_GREEN = '#2a6d5b';
  const BRAND_GREEN = '#42a57a';
  const RED_SOFT = '#b85a55';
  const SERIF = "'Fraunces', Georgia, serif";
  const SANS = "'Inter', system-ui, -apple-system, sans-serif";

  // Reusable button styles — declared inline so they can be passed to motion.button
  const primaryPill = (size: 'md' | 'lg' = 'md'): React.CSSProperties => ({
    background: BRAND_GREEN,
    color: '#ffffff',
    fontFamily: SANS,
    fontSize: size === 'lg' ? 22 : 18,
    fontWeight: 600,
    padding: size === 'lg' ? '16px 36px' : '12px 26px',
    borderRadius: 999,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 22px rgba(66,165,122,0.32)',
  });
  const outlinePill: React.CSSProperties = {
    background: 'transparent',
    color: BRAND_GREEN,
    border: `1.5px solid ${BRAND_GREEN}`,
    fontFamily: SANS,
    fontSize: 18,
    fontWeight: 600,
    padding: '12px 26px',
    borderRadius: 999,
    cursor: 'pointer',
  };
  const cardSurface: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e8e0c8',
    borderRadius: 24,
  };

  return (
    <div className="relative w-full h-full min-h-screen overflow-y-auto overflow-x-hidden" style={{ background: '#fbfbe8' }}>
      {/* Voice enable button — same anchor as before, just sits on cream now */}
      <div
        style={{
          position: 'absolute',
          top: 'clamp(160px, 18vw, 220px)',
          right: 20,
          zIndex: 40,
        }}
      >
        <VoiceEnableButton />
      </div>

      <div className="relative w-full min-h-screen">
        {/* INTRO STAGE */}
        {stage === 'intro' && (
          <div className="relative min-h-screen flex items-center justify-center py-10 md:py-16 px-4">
            <div className="text-center animate-fadeIn max-w-3xl">
              <motion.div
                className="mb-6 md:mb-10"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src={pekeIntro}
                  alt="Peke introduction"
                  className="mx-auto object-cover"
                  style={{
                    width: 'clamp(220px, 50vw, 360px)',
                    height: 'clamp(220px, 50vw, 360px)',
                    borderRadius: 32,
                    border: '1px solid #e8e0c8',
                    background: '#ffffff',
                  }}
                  loading="eager"
                  decoding="async"
                />
              </motion.div>

              <motion.h1
                className="animate-slideUp"
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(40px, 7vw, 84px)',
                  color: INK_GREEN,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.05,
                  marginBottom: 16,
                }}
              >
                {t.hello}
              </motion.h1>

              <p
                className="animate-slideUp delay-200 max-w-2xl mx-auto"
                style={{
                  fontFamily: SERIF,
                  fontStyle: 'italic',
                  fontSize: 'clamp(20px, 3vw, 32px)',
                  fontWeight: 500,
                  color: INK_GREEN,
                  marginBottom: 16,
                }}
              >
                {t.intro}
              </p>

              <p
                className="animate-slideUp delay-400 max-w-xl mx-auto"
                style={{
                  fontFamily: SANS,
                  fontSize: 'clamp(16px, 2.2vw, 22px)',
                  fontWeight: 500,
                  color: '#5f6e66',
                  marginBottom: 28,
                }}
              >
                {t.question}
              </p>

              <motion.button
                onClick={handleNextStage}
                className="animate-slideUp delay-600"
                style={primaryPill('lg')}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
              >
                {t.yes}
              </motion.button>
            </div>
          </div>
        )}

        {/* STORY CAROUSEL STAGE */}
        {stage === 'story' && (
          <div className="relative w-full min-h-screen overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="relative w-full min-h-screen flex flex-col"
              >
                {/* Image — same layout on mobile and desktop. The browser
                    picks 800w on phones, 1600w on desktop / retina. */}
                <div className="w-full px-3 pt-12 md:pt-8 md:px-4">
                  <img
                    src={storySlides[currentSlide].image}
                    srcSet={`${storySlides[currentSlide].imageSm} 800w, ${storySlides[currentSlide].image} 1600w`}
                    sizes="(max-width: 768px) 100vw, 896px"
                    alt={`Peke scene ${currentSlide + 1}`}
                    className="w-full max-w-3xl md:max-w-4xl aspect-[4/3] object-cover mx-auto"
                    style={{
                      borderRadius: 24,
                      border: '1px solid #e8e0c8',
                      background: '#ffffff',
                    }}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>

                {/* Controls card — same surface mobile + desktop */}
                <div className="w-full px-3 pb-8 md:px-8 md:pb-10 mt-5 md:mt-6">
                  <div
                    className="mx-auto p-5 md:p-8 max-w-3xl md:max-w-4xl"
                    style={cardSurface}
                  >
                    {/* Question */}
                    <h2
                      className="text-center"
                      style={{
                        fontFamily: SERIF,
                        fontWeight: 300,
                        fontSize: 'clamp(24px, 3.6vw, 44px)',
                        color: INK_GREEN,
                        letterSpacing: '-0.01em',
                        marginBottom: 8,
                      }}
                    >
                      {storySlides[currentSlide].question[language]}
                    </h2>

                    {/* Prompt — italic serif */}
                    <p
                      className="text-center"
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontSize: 'clamp(16px, 2.4vw, 24px)',
                        fontWeight: 500,
                        color: INK_GREEN,
                        marginBottom: 18,
                      }}
                    >
                      {storySlides[currentSlide].prompt[language]}
                    </p>

                    {/* Status line */}
                    {!hasRecorded[currentSlide] && !isRecording && (
                      <p
                        className="text-center mb-5"
                        style={{
                          fontFamily: SANS,
                          fontSize: 'clamp(13px, 1.5vw, 15px)',
                          fontWeight: 600,
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                          color: '#9a9a9a',
                        }}
                      >
                        {t.recordingInstructions}
                      </p>
                    )}

                    {hasRecorded[currentSlide] && !isRecording && (
                      <p
                        className="text-center mb-5"
                        style={{
                          fontFamily: SERIF,
                          fontStyle: 'italic',
                          fontSize: 'clamp(18px, 2.4vw, 24px)',
                          fontWeight: 500,
                          color: BRAND_GREEN,
                        }}
                      >
                        {t.awesome}
                      </p>
                    )}

                    {isRecording && (
                      <p
                        className="text-center mb-5 animate-pulse"
                        style={{
                          fontFamily: SERIF,
                          fontStyle: 'italic',
                          fontSize: 'clamp(18px, 2.4vw, 24px)',
                          fontWeight: 500,
                          color: RED_SOFT,
                        }}
                      >
                        {t.recording}
                      </p>
                    )}

                    {/* Mic button */}
                    <div className="flex justify-center mb-5">
                      <motion.button
                        onClick={isRecording ? stopRecording : startRecording}
                        className="rounded-full flex items-center justify-center"
                        style={{
                          width: 'clamp(76px, 14vw, 120px)',
                          height: 'clamp(76px, 14vw, 120px)',
                          background: isRecording ? RED_SOFT : BRAND_GREEN,
                          border: 'none',
                          cursor: 'pointer',
                          boxShadow: isRecording
                            ? '0 6px 22px rgba(184,90,85,0.35)'
                            : '0 6px 22px rgba(66,165,122,0.32)',
                        }}
                        whileHover={{ scale: 1.06 }}
                        whileTap={{ scale: 0.95 }}
                        animate={isRecording ? { scale: [1, 1.04, 1] } : { scale: 1 }}
                        transition={isRecording ? { duration: 1.2, repeat: Infinity } : {}}
                      >
                        <Mic style={{ width: 'clamp(28px, 5vw, 48px)', height: 'clamp(28px, 5vw, 48px)', color: '#ffffff' }} />
                      </motion.button>
                    </div>

                    {/* Playback + Next */}
                    {hasRecorded[currentSlide] && (
                      <motion.div
                        className="flex gap-3 md:gap-4 flex-wrap justify-center"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <motion.button
                          onClick={() => playRecording(currentSlide)}
                          style={{ ...outlinePill, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {isPlaying ? (
                            <>
                              <Pause style={{ width: 18, height: 18 }} />
                              <span>Stop</span>
                            </>
                          ) : (
                            <>
                              <Play style={{ width: 18, height: 18 }} />
                              <span>{t.playback}</span>
                            </>
                          )}
                        </motion.button>

                        <motion.button
                          onClick={handleNextSlide}
                          style={primaryPill('lg')}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {currentSlide < storySlides.length - 1 ? `${t.nextSlide} →` : `${t.createStory} →`}
                        </motion.button>
                      </motion.div>
                    )}

                    {/* Slide dots */}
                    {hasRecorded[currentSlide] && (
                      <motion.div
                        className="flex justify-center mt-5"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        <div className="inline-flex gap-2">
                          {storySlides.map((_, index) => (
                            <div
                              key={index}
                              style={{
                                width: index === currentSlide ? 18 : 8,
                                height: 8,
                                borderRadius: 999,
                                background:
                                  index === currentSlide
                                    ? BRAND_GREEN
                                    : index < currentSlide
                                      ? '#a4d6b8'
                                      : '#e0d8c0',
                                transition: 'all 200ms ease',
                              }}
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* FINAL STORY STAGE */}
        {stage === 'final-story' && (
          <div className="relative w-full min-h-screen overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={storyReviewSlide}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.4 }}
                className="relative w-full min-h-screen flex flex-col"
              >
                {/* Title — only on first review slide */}
                {storyReviewSlide === 0 && (
                  <motion.div
                    className="pt-12 md:pt-8 text-center px-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h1
                      style={{
                        fontFamily: SERIF,
                        fontWeight: 300,
                        fontSize: 'clamp(28px, 4.5vw, 56px)',
                        color: INK_GREEN,
                        letterSpacing: '-0.015em',
                      }}
                    >
                      {t.yourStoryTitle}
                    </h1>
                    <p
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontSize: 'clamp(15px, 1.8vw, 20px)',
                        color: INK_GREEN,
                        marginTop: 6,
                      }}
                    >
                      {t.storyIntro}
                    </p>
                  </motion.div>
                )}

                {/* Image — same responsive srcSet pattern as the story stage */}
                <div className="w-full px-3 pt-5 md:pt-6 md:px-4">
                  <img
                    src={storySlides[storyReviewSlide].image}
                    srcSet={`${storySlides[storyReviewSlide].imageSm} 800w, ${storySlides[storyReviewSlide].image} 1600w`}
                    sizes="(max-width: 768px) 100vw, 896px"
                    alt={`Story scene ${storyReviewSlide + 1}`}
                    className="w-full max-w-3xl md:max-w-4xl aspect-[4/3] object-cover mx-auto"
                    style={{
                      borderRadius: 24,
                      border: '1px solid #e8e0c8',
                      background: '#ffffff',
                    }}
                    loading="eager"
                    decoding="async"
                  />
                </div>

                {/* Controls card */}
                <div className="w-full px-3 pb-8 md:px-8 md:pb-10 mt-5 md:mt-6">
                  <div
                    className="mx-auto p-5 md:p-8 max-w-3xl md:max-w-4xl"
                    style={cardSurface}
                  >
                    <p
                      className="text-center"
                      style={{
                        fontFamily: SANS,
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: '#9a9a9a',
                        marginBottom: 8,
                      }}
                    >
                      {t.part} {storyReviewSlide + 1}
                    </p>
                    <h3
                      className="text-center"
                      style={{
                        fontFamily: SERIF,
                        fontStyle: 'italic',
                        fontSize: 'clamp(20px, 2.8vw, 30px)',
                        fontWeight: 500,
                        color: INK_GREEN,
                        marginBottom: 18,
                      }}
                    >
                      {storySlides[storyReviewSlide].question[language]}
                    </h3>

                    {/* Playback */}
                    {audioURLs[storyReviewSlide] && (
                      <div className="flex justify-center mb-4">
                        <motion.button
                          onClick={() => playRecording(storyReviewSlide)}
                          style={{ ...outlinePill, display: 'inline-flex', alignItems: 'center', gap: 8 }}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {isPlaying ? (
                            <>
                              <Pause style={{ width: 18, height: 18 }} />
                              <span>Stop</span>
                            </>
                          ) : (
                            <>
                              <Play style={{ width: 18, height: 18 }} />
                              <span>{t.playback}</span>
                            </>
                          )}
                        </motion.button>
                      </div>
                    )}

                    {/* Navigation */}
                    <div className="flex gap-3 justify-center flex-wrap">
                      {storyReviewSlide > 0 && (
                        <motion.button
                          onClick={handlePreviousStorySlide}
                          style={outlinePill}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          ← {t.previous}
                        </motion.button>
                      )}

                      {storyReviewSlide < storySlides.length - 1 ? (
                        <motion.button
                          onClick={handleNextStorySlide}
                          style={primaryPill()}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {t.next} →
                        </motion.button>
                      ) : (
                        <motion.button
                          onClick={handleComplete}
                          style={primaryPill('lg')}
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {t.continue} →
                        </motion.button>
                      )}
                    </div>

                    {/* Progress dots */}
                    <div className="flex justify-center mt-5">
                      <div className="inline-flex gap-2">
                        {storySlides.map((_, index) => (
                          <div
                            key={index}
                            style={{
                              width: index === storyReviewSlide ? 18 : 8,
                              height: 8,
                              borderRadius: 999,
                              background: index === storyReviewSlide ? BRAND_GREEN : '#a4d6b8',
                              transition: 'all 200ms ease',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* SURVEY STAGE */}
        {stage === 'survey' && (
          <div className="relative w-full min-h-screen">
            <PekeSurvey
              language={language}
              onComplete={handleSurveyComplete}
              gameDurationSeconds={getGameDurationSeconds()}
            />
          </div>
        )}

        {/* COMPLETION STAGE */}
        <AnimatePresence>
          {stage === 'complete' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative w-full min-h-screen flex items-center justify-center z-50 px-4 py-10"
            >
              <motion.div
                className="p-6 sm:p-10 md:p-14 mx-4 text-center w-full max-w-2xl"
                style={cardSurface}
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              >
                {/* Celebration Peke */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <img
                    src={pekeCelebration}
                    alt="Peke celebrating"
                    className="object-cover mx-auto"
                    style={{
                      width: 'clamp(140px, 30vw, 240px)',
                      height: 'clamp(140px, 30vw, 240px)',
                      borderRadius: '50%',
                      border: '4px solid #dcecda',
                      background: '#ffffff',
                    }}
                    loading="lazy"
                    decoding="async"
                  />
                </motion.div>

                <motion.h2
                  className="mt-6"
                  style={{
                    fontFamily: SERIF,
                    fontWeight: 300,
                    fontSize: 'clamp(32px, 6vw, 64px)',
                    color: INK_GREEN,
                    letterSpacing: '-0.015em',
                  }}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  {t.wonderful}
                </motion.h2>

                <motion.p
                  className="mt-3"
                  style={{
                    fontFamily: SERIF,
                    fontStyle: 'italic',
                    fontSize: 'clamp(18px, 2.4vw, 26px)',
                    fontWeight: 500,
                    color: INK_GREEN,
                  }}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {t.thankYou}
                </motion.p>

                <motion.p
                  className="mt-2"
                  style={{
                    fontFamily: SANS,
                    fontSize: 'clamp(15px, 1.8vw, 18px)',
                    fontWeight: 500,
                    color: '#5f6e66',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                >
                  {t.comeBack}
                </motion.p>

                <motion.div
                  className="flex gap-3 justify-center mt-7 flex-wrap"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <motion.button
                    onClick={handleReplay}
                    style={primaryPill('lg')}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {t.again}
                  </motion.button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Confetti — celebratory but lighter than before. Smaller, fewer,
            and uses brand-tone glyphs only so it reads as part of the
            design system instead of a rainbow burst. */}
        {(stage === 'final-story' || stage === 'complete' || stage === 'survey') && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', lineHeight: 1 }}
                initial={{
                  x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : 0,
                  y: -50,
                  rotate: 0,
                  opacity: 0.85,
                }}
                animate={{
                  y: typeof window !== 'undefined' ? window.innerHeight + 50 : 1000,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                  x:
                    typeof window !== 'undefined'
                      ? Math.random() * window.innerWidth
                      : 0,
                }}
                transition={{
                  duration: 3.5 + Math.random() * 2,
                  delay: Math.random() * 0.8,
                  ease: 'linear',
                }}
              >
                {['🐾', '🌿', '✨'][Math.floor(Math.random() * 3)]}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden canvas for future video generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
        .animate-slideUp { animation: slideUp 0.6s ease-out; }
        .animate-slideUp.delay-200 { animation-delay: 0.15s; opacity: 0; animation-fill-mode: forwards; }
        .animate-slideUp.delay-400 { animation-delay: 0.3s; opacity: 0; animation-fill-mode: forwards; }
        .animate-slideUp.delay-600 { animation-delay: 0.45s; opacity: 0; animation-fill-mode: forwards; }
      `}</style>
    </div>
  );
}
